import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { RefreshCw } from "lucide-react";
import { ExclamationTriangleIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import CountryFlag from "../../components/common/CountryFlag";
import { AxiosError } from "axios";
import { errorHandler } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";

type Level = "" | "error" | "critical" | "crash";
type Status = "open" | "resolved" | "";

const levelBadge: Record<string, { bg: string; text: string }> = {
    error:    { bg: "#f59e0b", text: "#fff" },
    critical: { bg: "#ef4444", text: "#fff" },
    crash:    { bg: "#7c3aed", text: "#fff" },
};

const timeAgo = (date: string | Date): string => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
};

const formatLocation = (loc?: { city?: string; region?: string; country?: string }): string => {
    if (!loc) return "";
    return [loc.city, loc.region, loc.country].filter(Boolean).join(", ");
};

const SystemErrorsPage: React.FC = () => {
    const { showToast }: any = useToast();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [data, setData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);

    const [search, setSearch] = useState("");
    const [levelFilter, setLevelFilter] = useState<Level>("");
    const [statusFilter, setStatusFilter] = useState<Status>("open");

    const [selectedError, setSelectedError] = useState<any>(null);
    const [resolvingId, setResolvingId] = useState<string | null>(null);
    const [resolvingAll, setResolvingAll] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const startIndex = currentPage * itemsPerPage;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const levelOptions = [
        { label: "All Levels", value: "" },
        { label: "Error", value: "error" },
        { label: "Critical", value: "critical" },
        { label: "Crash", value: "crash" },
    ];

    const statusOptions = [
        { label: "Open", value: "open" },
        { label: "Resolved", value: "resolved" },
        { label: "All Statuses", value: "" },
    ];

    async function getErrors() {
        try {
            setDataLoad(true);
            const params: any = { page: currentPage + 1, limit: itemsPerPage };
            if (levelFilter) params.level = levelFilter;
            if (statusFilter) params.status = statusFilter;
            if (search.trim().length > 1) params.search = search.trim();

            await apiFunctions
                .get("/systemerror", { params })
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        setTotalRecords(response.data.totalRecords || 0);
                        setTotalPages(Math.ceil((response.data.totalRecords || 0) / itemsPerPage));
                        setData(response.data.data || []);
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response.data.message, life: 3000 });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getErrors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!load) getErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, levelFilter, statusFilter, search]);

    const handlePageChange = (newPage: number) => setCurrentPage(newPage);
    const handleItemsPerPageChange = (value: number) => { setItemsPerPage(value); setCurrentPage(0); };
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setCurrentPage(0); };
    const handleLevelChange = (value: Level) => { setLevelFilter(value); setCurrentPage(0); };
    const handleStatusChange = (value: Status) => { setStatusFilter(value); setCurrentPage(0); };

    const handleRefresh = async () => {
        setRefreshing(true);
        await getErrors();
        setRefreshing(false);
    };

    const handleResolve = async (id: string) => {
        setResolvingId(id);
        try {
            await apiFunctions.put(`/systemerror/resolve/${id}`, {});
            showToast({ severity: "success", summary: "Resolved", detail: "Marked as resolved", life: 2000 });
            setSelectedError(null);
            getErrors();
        } catch (err) { errorHandler(err as AxiosError); }
        finally { setResolvingId(null); }
    };

    const handleResolveAll = async () => {
        setResolvingAll(true);
        try {
            await apiFunctions.put("/systemerror/resolveall", {});
            showToast({ severity: "success", summary: "Done", detail: "All errors resolved", life: 2000 });
            getErrors();
        } catch (err) { errorHandler(err as AxiosError); }
        finally { setResolvingAll(false); }
    };

    const levelBodyTemplate = (row: any) => {
        const lvl = row.level || "error";
        const badge = levelBadge[lvl] || { bg: "#6b7280", text: "#fff" };
        return (
            <span style={{ background: badge.bg, color: badge.text, borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em" }}>
                {lvl.toUpperCase()}
            </span>
        );
    };

    const statusBodyTemplate = (row: any) => (
        <span style={{
            background: row.status === "open" ? "#fee2e2" : "#d1fae5",
            color: row.status === "open" ? "#b91c1c" : "#065f46",
            borderRadius: "4px",
            padding: "2px 8px",
            fontSize: "11px",
            fontWeight: 600,
        }}>
            {row.status === "open" ? "Open" : "Resolved"}
        </span>
    );

    const messageBodyTemplate = (row: any) => (
        <span style={{ display: "block", maxWidth: "320px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.message}>
            {row.message}
        </span>
    );

    const routeBodyTemplate = (row: any) => row.route ? (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
            <span style={{ fontWeight: 600, color: "#374151" }}>{row.method}</span> {row.route}
        </span>
    ) : "-";

    const userBodyTemplate = (row: any) => (row.user?.name || row.user?.mobileno) ? (
        <span>{row.user.name || row.user.mobileno}</span>
    ) : "-";

    const ipBodyTemplate = (row: any) => row.ip ? (
        <>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
                <CountryFlag countryCode={row.location?.country} />
                <span style={{ fontFamily: "monospace" }}>{row.ip}</span>
            </span>
            {formatLocation(row.location) && <div className="text-xs" style={{ color: "#9ca3af" }}>{formatLocation(row.location)}</div>}
        </>
    ) : "-";

    const timeBodyTemplate = (row: any) => (
        <span title={new Date(row.createddate).toLocaleString("en-IN")}>{timeAgo(row.createddate)}</span>
    );

    const actionBodyTemplate = (row: any) => (
        <div className="action-icons">
            <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => setSelectedError(row)} />
        </div>
    );

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">
            <div className="col-span-12 sm:col-span-5 flex flex-col gap-1">
                <h2 className="card-title m-0 text-lg sm:text-xl flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-6 h-6" style={{ color: "#f59e0b" }} />
                    System Errors
                </h2>
                <p className="m-0" style={{ fontSize: "13px", color: "#6b7280" }}>
                    {totalRecords} error{totalRecords !== 1 ? "s" : ""} found
                </p>
            </div>
            <div className="col-span-12 sm:col-span-7 flex flex-col sm:flex-row gap-2 sm:justify-end flex-wrap">
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        value={search}
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by message, route, or user..."
                        onChange={handleSearchChange}
                    />
                </div>
                <Dropdown value={levelFilter} options={levelOptions} optionLabel="label" optionValue="value" onChange={(e) => handleLevelChange(e.value)} className="w-full sm:w-auto" />
                <Dropdown value={statusFilter} options={statusOptions} optionLabel="label" optionValue="value" onChange={(e) => handleStatusChange(e.value)} className="w-full sm:w-auto" />
                <Button
                    icon={<RefreshCw size={18} strokeWidth={2.2} />}
                    className="w-11 h-11 flex items-center justify-center"
                    severity="info"
                    outlined
                    rounded
                    loading={refreshing}
                    onClick={handleRefresh}
                    title="Refresh"
                />
                <Button label="Resolve All" icon={<CheckCircleIcon className="w-5 h-5 mr-1" />} severity="success" outlined loading={resolvingAll} onClick={handleResolveAll} />
            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];
            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown value={itemsPerPage} options={dropdownOptions} onChange={(e) => handleItemsPerPageChange(e.value)} />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => <span className="entries-text mx-1">entries</span>,
    };

    return (
        <div className="dashboard-margin">
            {isMobile ? (
                <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                    <div className="p-3 pb-2">{header}</div>
                    <div className="flex flex-col gap-2 px-3 pb-3">
                        {dataLoad ? (
                            Array.from({ length: itemsPerPage }, (_, i) => (
                                <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                    <Skeleton width="60%" height="1rem" className="mb-2" />
                                    <Skeleton width="40%" height="0.8rem" />
                                </div>
                            ))
                        ) : data.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <ExclamationTriangleIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No errors found</p>
                            </div>
                        ) : (
                            data.map((row: any) => (
                                <div key={row._id} className="border rounded-lg p-3 w-full box-border cursor-pointer" style={{ borderColor: "#e5e7eb" }} onClick={() => setSelectedError(row)}>
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        {levelBodyTemplate(row)}
                                        {statusBodyTemplate(row)}
                                    </div>
                                    <p className="m-0 font-semibold text-sm break-words" style={{ color: "#111827" }}>{row.message}</p>
                                    <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                        {row.route && <div>{row.method} {row.route}</div>}
                                        {(row.user?.name || row.user?.mobileno) && <div>{row.user.name || row.user.mobileno}</div>}
                                        <div>{timeAgo(row.createddate)}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <DataTable
                    scrollable
                    scrollHeight="calc(100vh - 260px)"
                    style={{ minHeight: "calc(100vh - 185px)", background: "#fff" }}
                    header={header}
                    value={dataLoad ? Array.from({ length: itemsPerPage }, (_, i) => ({ _skeletonId: i })) : data}
                    dataKey={dataLoad ? "_skeletonId" : "_id"}
                    resizableColumns
                    rows={itemsPerPage}
                    emptyMessage={
                        <div style={{ textAlign: "center", padding: "64px 0" }}>
                            <ExclamationTriangleIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No errors found</p>
                        </div>
                    }
                >
                    <Column field="level" header="Level" body={(row) => dataLoad ? <Skeleton /> : levelBodyTemplate(row)} style={{ width: "100px" }} />
                    <Column field="status" header="Status" body={(row) => dataLoad ? <Skeleton /> : statusBodyTemplate(row)} style={{ width: "100px" }} />
                    <Column field="message" header="Message" body={(row) => dataLoad ? <Skeleton /> : messageBodyTemplate(row)} />
                    <Column field="route" header="Route" body={(row) => dataLoad ? <Skeleton /> : routeBodyTemplate(row)} />
                    <Column field="user" header="User" body={(row) => dataLoad ? <Skeleton /> : userBodyTemplate(row)} />
                    <Column field="ip" header="IP" body={(row) => dataLoad ? <Skeleton /> : ipBodyTemplate(row)} />
                    <Column field="createddate" header="Time" body={(row) => dataLoad ? <Skeleton /> : timeBodyTemplate(row)} />
                    <Column header="Action" className="action-column" frozen alignFrozen="right" style={{ minWidth: "100px" }} body={(row) => dataLoad ? <Skeleton /> : actionBodyTemplate(row)} />
                </DataTable>
            )}

            {totalRecords > 0 && (
                <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator template={paginatoreTemplate} first={startIndex} rows={itemsPerPage} totalRecords={totalRecords} />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button onClick={() => handlePageChange(currentPage - 1)} className="pagination-button flex-shrink-0 px-2 sm:px-4" disabled={currentPage === 0}>
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>
                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">Page {currentPage + 1} of {totalPages || 1}</span>
                        </Button>
                        <Button onClick={() => handlePageChange(currentPage + 1)} className="pagination-button flex-shrink-0 px-2 sm:px-4" disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}>
                            Next
                        </Button>
                    </div>
                </div>
            )}

            <Dialog
                header={selectedError ? `#${selectedError._id?.slice(-6).toUpperCase()} — ${(selectedError.level || "error").toUpperCase()}` : "Error"}
                visible={!!selectedError}
                style={{ width: "90vw", maxWidth: "700px" }}
                modal
                draggable={false}
                onHide={() => setSelectedError(null)}
            >
                {selectedError && (
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2">
                            {statusBodyTemplate(selectedError)}
                            <span style={{ fontSize: "12px", color: "#6b7280" }}>
                                {timeAgo(selectedError.createddate)} ({new Date(selectedError.createddate).toLocaleString("en-IN")})
                            </span>
                        </div>

                        <p className="m-0" style={{ fontWeight: 600, fontSize: "15px", color: "#111827" }}>{selectedError.message}</p>

                        {(selectedError.user?.mobileno || selectedError.user?.name || selectedError.ip || selectedError.route) && (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
                                {(selectedError.user?.mobileno || selectedError.user?.name) && (
                                    <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "8px 12px" }}>
                                        <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>Account</p>
                                        <p className="m-0" style={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{selectedError.user.name || selectedError.user.mobileno}</p>
                                        {selectedError.user.name && selectedError.user.mobileno && <p className="m-0" style={{ fontSize: "12px", color: "#6b7280" }}>{selectedError.user.mobileno}</p>}
                                    </div>
                                )}
                                {selectedError.ip && (
                                    <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "8px 12px" }}>
                                        <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>IP Address</p>
                                        <p className="m-0" style={{ fontSize: "13px", color: "#111827", fontFamily: "monospace", wordBreak: "break-all", display: "flex", alignItems: "center" }}>
                                            <CountryFlag countryCode={selectedError.location?.country} />
                                            {selectedError.ip}
                                        </p>
                                        {formatLocation(selectedError.location) && <p className="m-0" style={{ fontSize: "12px", color: "#6b7280" }}>{formatLocation(selectedError.location)}</p>}
                                    </div>
                                )}
                                {selectedError.route && (
                                    <div style={{ gridColumn: "1 / -1", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "8px 12px" }}>
                                        <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>API Route</p>
                                        <p className="m-0" style={{ fontSize: "13px", color: "#111827", fontFamily: "monospace", wordBreak: "break-all", overflowWrap: "anywhere" }}>{selectedError.method} {selectedError.route}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedError.stack && (
                            <div>
                                <p className="m-0 mb-1" style={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Stack Trace</p>
                                <pre style={{ background: "#1e1e1e", color: "#d4d4d4", borderRadius: "6px", padding: "12px", fontSize: "12px", overflowX: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: "300px", overflowY: "auto" }}>
                                    {selectedError.stack}
                                </pre>
                            </div>
                        )}

                        {selectedError.body && Object.keys(selectedError.body).length > 0 && (
                            <div>
                                <p className="m-0 mb-1" style={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>req.body</p>
                                <pre style={{ background: "#1e1e1e", color: "#d4d4d4", borderRadius: "6px", padding: "12px", fontSize: "12px", overflowX: "auto", margin: 0, maxHeight: "200px", overflowY: "auto" }}>
                                    {JSON.stringify(selectedError.body, null, 2)}
                                </pre>
                            </div>
                        )}

                        {selectedError.context && Object.keys(selectedError.context).length > 0 && (
                            <div>
                                <p className="m-0 mb-1" style={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Context</p>
                                <pre style={{ background: "#1e1e1e", color: "#d4d4d4", borderRadius: "6px", padding: "12px", fontSize: "12px", overflowX: "auto", margin: 0, maxHeight: "200px", overflowY: "auto" }}>
                                    {JSON.stringify(selectedError.context, null, 2)}
                                </pre>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button label="Close" severity="secondary" onClick={() => setSelectedError(null)} />
                            {selectedError.status === "open" && (
                                <Button
                                    label="Mark as Resolved"
                                    icon="pi pi-check-circle"
                                    severity="success"
                                    loading={resolvingId === selectedError._id}
                                    onClick={() => handleResolve(selectedError._id)}
                                />
                            )}
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default SystemErrorsPage;
